import { Fingerprint, KeyRound, ShieldCheck } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Button } from '../atoms/Button'

/**
 * WebAuthnVerifyStep — the screen the user sees when the pipeline runtime
 * pauses at `mfa.verify` with `factor: "webauthn_platform"` or
 * `"webauthn_security_key"`. Mirrors WebAuthnEnrollStep but drives the
 * authentication ceremony (`navigator.credentials.get`) instead of
 * registration (`navigator.credentials.create`).
 *
 * Like WebAuthnEnrollStep, the component is **headless of the WebAuthn
 * API**. The actual `navigator.credentials.get` call lives in the parent
 * (which already has `@simplewebauthn/browser` installed in dp-auth-ui).
 * The molecule owns the visible state machine; the parent owns the
 * browser API call + DOMException → user-copy mapping.
 *
 * What the molecule owns:
 *  - the icon + heading + helper copy that frame the ceremony
 *  - a "Use" button that triggers `onAssert()`
 *  - the visible state transitions (idle → running → success → error)
 *  - retry affordance when the ceremony fails
 *  - a11y semantics on the live status region
 *
 * What the parent owns:
 *  - the WebAuthn API call (and the challenge it sends in)
 *  - transport encoding of the assertion
 *  - mapping `NotAllowedError` / `InvalidStateError` to user copy
 */

/**
 * Ceremony status — the molecule's state machine.
 *
 *   idle    → user hasn't started yet (initial)
 *   running → onAssert() is in flight; "use your authenticator now"
 *   success → onAssert() resolved successfully; success copy + green
 *             icon shown until the parent navigates away
 *   error   → onAssert() rejected; error copy + retry button shown
 */
export type WebAuthnVerifyStatus = 'idle' | 'running' | 'success' | 'error'

export interface WebAuthnVerifyStepProps {
  /**
   * The kind of authenticator the parent is asking the user to use.
   * Drives icon + helper copy. Both kinds use the same DOM structure;
   * only the copy + glyph differ.
   *
   *   "platform"     → built-in (Face ID, Windows Hello, Touch ID)
   *   "security-key" → roaming (YubiKey, FIDO2 USB / NFC token)
   */
  kind?: 'platform' | 'security-key'
  /**
   * Triggered when the user clicks Use (or Retry after an error). The
   * parent runs `navigator.credentials.get` with the challenge and
   * returns the assertion; the molecule only awaits + flips its visible
   * state.
   */
  onAssert: () => Promise<void>
  /**
   * Called when the user clicks the "Use a different method" button.
   * Optional — when absent, no cancel is rendered (a fallback / re-pick
   * affordance is the parent's choice).
   */
  onCancel?: () => void
  /**
   * Error message surfaced when the ceremony fails. The parent maps the
   * raw `DOMException` (NotAllowedError, InvalidStateError, etc.) into
   * user copy before passing it here. Presence of `error` flips the
   * visible status to "error".
   */
  error?: string
  /**
   * Override the visible heading. Default per `kind`:
   *   platform     → "Verify with your device"
   *   security-key → "Verify with your security key"
   */
  heading?: string
  /** Optional helper text rendered under the heading. */
  helperText?: string
  /** Extra classes for the wrapper. */
  className?: string
}

const HEADING_DEFAULTS: Record<NonNullable<WebAuthnVerifyStepProps['kind']>, string> = {
  platform: 'Verify with your device',
  'security-key': 'Verify with your security key',
}

const HELPER_DEFAULTS: Record<NonNullable<WebAuthnVerifyStepProps['kind']>, string> = {
  platform:
    'Use Face ID, Touch ID, Windows Hello, or whichever sign-in your device offers. We never see your biometrics.',
  'security-key': 'Insert your security key now. You may be asked to touch it to confirm.',
}

export const WebAuthnVerifyStep = React.forwardRef<HTMLDivElement, WebAuthnVerifyStepProps>(
  ({ kind = 'platform', onAssert, onCancel, error, heading, helperText, className }, ref) => {
    const [status, setStatus] = React.useState<WebAuthnVerifyStatus>('idle')

    // Reflect parent-supplied error into the visible status. We don't
    // own `error` so we don't clear it — the parent does on retry.
    React.useEffect(() => {
      if (error) setStatus('error')
    }, [error])

    const handleStart = React.useCallback(async () => {
      if (status === 'running') return
      setStatus('running')
      try {
        await onAssert()
        setStatus('success')
      } catch {
        // The parent surfaces the error via the `error` prop; we only
        // flip the visible status here. Re-throwing would leak the raw
        // DOMException into the React event handler chain.
        setStatus('error')
      }
    }, [onAssert, status])

    const resolvedHeading = heading ?? HEADING_DEFAULTS[kind]
    const resolvedHelper = helperText ?? HELPER_DEFAULTS[kind]

    const Icon = kind === 'platform' ? Fingerprint : KeyRound
    const buttonLabel =
      status === 'running'
        ? 'Waiting for authenticator…'
        : status === 'error'
          ? 'Try again'
          : status === 'success'
            ? 'Verified'
            : kind === 'platform'
              ? 'Use device sign-in'
              : 'Use security key'

    return (
      <div ref={ref} className={cn('flex flex-col items-center gap-5 text-center', className)}>
        <div className="flex flex-col items-center gap-1">
          <h2 className="text-heading-sm font-semibold text-ia-text">{resolvedHeading}</h2>
          {resolvedHelper ? (
            <p className="text-body-sm text-ia-text-secondary">{resolvedHelper}</p>
          ) : null}
        </div>

        {/* Status glyph. Color flips on success (green) and error (red);
            idle and running share the neutral primary tone so the user's
            attention stays on the ceremony copy + button below. */}
        <div
          className={cn(
            'flex h-20 w-20 items-center justify-center rounded-full border',
            status === 'success'
              ? 'border-ia-success bg-ia-success/10 text-ia-success'
              : status === 'error'
                ? 'border-ia-error bg-ia-error/10 text-ia-error'
                : 'border-ia-border bg-surface text-primary',
          )}
          aria-hidden
        >
          {status === 'success' ? (
            <ShieldCheck className="h-10 w-10" />
          ) : (
            <Icon className="h-10 w-10" />
          )}
        </div>

        {/* Live region: announces status changes for AT users. Kept
            mounted (empty when idle) so SR cadence stays deterministic
            across status transitions. */}
        <div
          role="status"
          aria-live="polite"
          className={cn(
            'min-h-5 text-body-sm',
            status === 'success'
              ? 'text-ia-success'
              : status === 'error'
                ? 'text-ia-error'
                : 'text-ia-text-secondary',
          )}
        >
          {status === 'running' && 'Waiting for your authenticator…'}
          {status === 'success' && 'Verified.'}
          {status === 'error' && (error || 'Something went wrong. Try again.')}
          {status === 'idle' && ' '}
        </div>

        <div className="flex w-full flex-col gap-2">
          <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={handleStart}
            disabled={status === 'running' || status === 'success'}
            loading={status === 'running'}
            className="w-full"
          >
            {buttonLabel}
          </Button>
          {onCancel ? (
            <Button
              type="button"
              variant="secondary"
              size="lg"
              onClick={onCancel}
              disabled={status === 'running'}
              className="w-full"
            >
              Use a different method
            </Button>
          ) : null}
        </div>
      </div>
    )
  },
)
WebAuthnVerifyStep.displayName = 'WebAuthnVerifyStep'
